import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Linkedin, Instagram, Star, Quote, ExternalLink } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const instagramEmbed = (url) => {
  const m = String(url).match(/instagram\.com\/(p|reel|tv)\/([A-Za-z0-9_-]{5,})/i);
  if (!m) return null;
  return `https://www.instagram.com/${m[1]}/${m[2]}/embed/?cr=1&v=14`;
};

const linkedinEmbed = (url) => {
  const activity = String(url).match(/activity[-:](\d{6,})/i);
  if (activity) return `https://www.linkedin.com/embed/feed/update/urn:li:activity:${activity[1]}`;
  const share = String(url).match(/urn:li:share:(\d+)/i);
  if (share) return `https://www.linkedin.com/embed/feed/update/urn:li:share:${share[1]}`;
  if (String(url).includes('/embed/feed/update/')) return url;
  return null;
};

const BisonFeedCarousel = () => {
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({ linkedin_url: '', instagram_url: '', linkedin_posts: [], instagram_posts: [] });

  useEffect(() => {
    axios.get(`${API_URL}/landing/settings/public`).then((r) => setSettings(r.data)).catch(() => {});
    axios.get(`${API_URL}/landing/reviews/public`).then((r) => { if (r.data.enabled) setReviews(r.data.reviews || []); }).catch(() => {});
  }, []);

  const linkedinCards = useMemo(() =>
    (settings.linkedin_posts || []).map((url) => ({ url, embed: linkedinEmbed(url) })).filter((c) => c.embed),
  [settings.linkedin_posts]);

  const instagramCards = useMemo(() =>
    (settings.instagram_posts || []).map((url) => ({ url, embed: instagramEmbed(url) })).filter((c) => c.embed),
  [settings.instagram_posts]);

  const hasAnyContent = linkedinCards.length > 0 || instagramCards.length > 0 || reviews.length > 0;
  if (!hasAnyContent) return null;

  const renderColumn = (kind, cards, title, icon, tintCls) => (
    <div data-testid={`bison-feed-${kind}-col`}>
      <div className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border mb-4 ${tintCls}`}>
        {icon}
        {title}
      </div>
      {cards.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-sm text-muted-foreground text-center bg-white/60">
          Latest posts will appear here once linked in the admin dashboard.
        </div>
      ) : (
        <div className="space-y-6">
          {cards.map((c, i) => (
            <div key={c.embed || i} className="rounded-2xl border border-border bg-white overflow-hidden shadow-sm hover:shadow-md transition" data-testid={`feed-${kind}-embed`}>
              <iframe
                src={c.embed}
                title={`${kind} post`}
                loading="lazy"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                style={{ width: '100%', minHeight: kind === 'linkedin' ? 620 : 640, border: 0, display: 'block' }}
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a href={c.url} target="_blank" rel="noreferrer" className="flex items-center justify-end gap-1 text-xs text-muted-foreground p-2 hover:text-foreground">
                Open post <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <section id="bison-feed" className="py-20 bg-gradient-to-b from-white to-emerald-50/40" data-testid="bison-feed">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 mb-2 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold uppercase tracking-wider">
            Live from Bison
          </span>
          <h2 className="font-heading font-bold text-4xl">What&apos;s happening right now</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl mx-auto">
            Live posts pulled straight from our LinkedIn and Instagram — plus the latest reviews from customers.
          </p>
          <div className="flex justify-center gap-4 mt-4 text-sm">
            {settings.linkedin_url && (
              <a href={settings.linkedin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-700 hover:underline" data-testid="bison-feed-linkedin-link">
                <Linkedin className="w-4 h-4" /> Follow on LinkedIn
              </a>
            )}
            {settings.instagram_url && (
              <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-pink-600 hover:underline" data-testid="bison-feed-instagram-link">
                <Instagram className="w-4 h-4" /> Follow on Instagram
              </a>
            )}
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-2 mb-10">
          {renderColumn('linkedin', linkedinCards, 'From LinkedIn', <Linkedin className="w-5 h-5" />, 'text-blue-700 bg-blue-50 border-blue-100')}
          {renderColumn('instagram', instagramCards, 'From Instagram', <Instagram className="w-5 h-5" />, 'text-pink-600 bg-pink-50 border-pink-100')}
        </div>

        {reviews.length > 0 && (
          <div data-testid="bison-feed-reviews">
            <h3 className="text-center font-heading text-lg mb-4 text-muted-foreground">Recent customer reviews</h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {reviews.slice(0, 6).map((r) => (
                <article key={r.id} className="rounded-2xl border border-border shadow-sm p-5 bg-white flex flex-col justify-between min-h-[180px]" data-testid={`feed-review-${r.id}`}>
                  <div>
                    <div className="flex items-center gap-0.5 mb-2" aria-label={`Rating ${r.rating} out of 5`}>
                      {Array.from({ length: 5 }, (_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < r.rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`} />
                      ))}
                    </div>
                    <Quote className="w-5 h-5 text-emerald-600/40 mb-2" />
                    <p className="text-sm text-foreground italic leading-relaxed line-clamp-6">&ldquo;{r.comment}&rdquo;</p>
                  </div>
                  <p className="text-xs font-medium text-muted-foreground mt-4">— {r.name}</p>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default BisonFeedCarousel;
